import { apiFetch } from "../context/AppContext";
import { exportSubmissionExcel, exportSubmissionPdf, safeExportFilename } from "./exportSubmissionPdf";

function fileLabel(file) {
  return file?.original_name || file?.file_name || "";
}

function evidenceNames(files) {
  return (files || []).map(fileLabel).filter(Boolean).join("; ");
}

export function buildGapEvidenceRows(submission, attachmentsByQuestion = {}) {
  const recs = submission?.result?.recommendations || [];
  if (recs.length) {
    return recs.map((rec, index) => {
      const questionId = rec.question_id || "";
      const files = rec.evidence?.length
        ? rec.evidence
        : attachmentsByQuestion[questionId] || [];
      return {
        question_id: questionId || `Item ${index + 1}`,
        section: rec.section_title || "",
        question: rec.question_text || "",
        answer: rec.answer || submission?.answers?.[questionId] || "",
        comment: rec.comment || submission?.notes?.[questionId] || "",
        owner: rec.owner || submission?.owners?.[questionId] || "",
        evidence: evidenceNames(files),
      };
    });
  }

  const notes = submission?.notes || {};
  const owners = submission?.owners || {};
  const answers = submission?.answers || {};
  const ids = new Set([
    ...Object.keys(notes),
    ...Object.keys(owners),
    ...Object.keys(attachmentsByQuestion),
  ]);
  return [...ids]
    .filter((id) => notes[id] || owners[id] || (attachmentsByQuestion[id] || []).length)
    .map((id) => ({
      question_id: id,
      section: "",
      question: "",
      answer: answers[id] || "",
      comment: notes[id] || "",
      owner: owners[id] || "",
      evidence: evidenceNames(attachmentsByQuestion[id]),
    }));
}

export function buildGapEvidenceExportSections(submission, attachmentsByQuestion = {}) {
  const rows = buildGapEvidenceRows(submission, attachmentsByQuestion);
  const submittedAt = submission?.submitted_at || submission?.created_at;
  return [
    {
      heading: "Comments & Evidence",
      rows: [
        { label: "Organisation", value: submission?.org_name || "—" },
        { label: "Department", value: submission?.department_name || "—" },
        { label: "Submitted by", value: submission?.submitted_by_name || "—" },
        {
          label: "Submitted",
          value: submittedAt ? new Date(submittedAt).toLocaleString("en-IN") : "—",
        },
        {
          label: "Compliance score",
          value: submission?.score != null ? `${submission.score}%` : "—",
        },
        {
          label: "Items with comments or evidence",
          value: String(rows.length),
        },
      ],
      table: {
        headers: ["Question ID", "Section", "Question", "Answer", "Comment", "Owner", "Evidence files"],
        rows: rows.length
          ? rows.map((row) => [
              row.question_id,
              row.section,
              row.question,
              row.answer,
              row.comment,
              row.owner,
              row.evidence || "—",
            ])
          : [["—", "—", "No comments or evidence recorded", "—", "—", "—", "—"]],
      },
    },
  ];
}

function evidenceExportPayload(submission, attachmentsByQuestion = {}) {
  const orgName = submission?.org_name || "Gap_Assessment";
  const dept = submission?.department_name || "assessment";
  return {
    title: `${orgName} — Comments & Evidence`,
    subtitle: `Gap Assessment${dept ? ` · ${dept}` : ""}`,
    module: "Gap Assessment",
    organization: orgName,
    filename: `Gap_Evidence_${safeExportFilename(dept || orgName)}`,
    sections: buildGapEvidenceExportSections(submission, attachmentsByQuestion),
  };
}

export async function exportGapEvidencePdf(submission, attachmentsByQuestion = {}) {
  await exportSubmissionPdf(evidenceExportPayload(submission, attachmentsByQuestion));
}

export async function exportGapEvidenceExcel(submission, attachmentsByQuestion = {}) {
  await exportSubmissionExcel(evidenceExportPayload(submission, attachmentsByQuestion));
}

export async function fetchGapSubmission(submissionId) {
  const res = await apiFetch(`/gap-assessment/submissions/${submissionId}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to load submission");
  }
  return res.json();
}
